import React from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";

import { baseAxios } from "../../../api/api";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { useDispatchWrapper } from "../../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../../redux/messages/messagesActions";
import { CustomAlert } from "../../../components/formsComponents/CustomAlert";
import { ILocationResponsePayload } from "../../../components/LocationAutoComplete/LocationAutoComplete";
import { uploadOnboardingFile } from "../../../components/FileUpload/utils";
import { REACT_APP_NAV_ROUTES } from "../../../router/AppNavigation";
import { COUNTRIES, ICountry } from "../../../constants/countries";

import { decodeUserToken } from "../SignUp/helpers/decodeUserToken";

import {
  buildOtpSubtitle,
  businessOnboardStepLabels,
  businessOnboardSteps,
  createEmptyVenue,
  initialFormValues,
  mobileOtpMeta,
  ONBOARD_STORAGE_KEY,
  primaryCtaLabels,
} from "./BusinessOnboard.constant";
import {
  IMobileData,
  ISignupFormValues,
  IVenue,
  VenueErrors,
} from "./BusinessOnboard.types";
import { OnboardLayout } from "./components/OnboardLayout";
import { OnboardStepper } from "./components/OnboardStepper";
import { StepHeader } from "./components/StepHeader";
import { StepFooter } from "./components/StepFooter";
import { MobileStep } from "./steps/MobileStep";
import { OtpStep } from "./steps/OtpStep";
import { CompanyStep } from "./steps/CompanyStep";
import { VenueStep } from "./steps/VenueStep";
import { ProfileStep } from "./steps/ProfileStep";

const TOTAL_STEPS = 4;

interface IPersistedProgress {
  activeStep: number;
  showOTP: boolean;
  selectedCountryIso: string;
  mobileData: IMobileData;
  values: ISignupFormValues;
  venues: IVenue[];
}

/** Reads the persisted onboarding snapshot from sessionStorage (if any). */
const readPersistedProgress = (): Partial<IPersistedProgress> | null => {
  try {
    const raw = sessionStorage.getItem(ONBOARD_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<IPersistedProgress>) : null;
  } catch {
    return null;
  }
};

/**
 * Smart container for the v2 Banquet First onboarding flow. Owns all state,
 * validation, and API calls; renders the layout + stepper + active step + footer.
 * Presentational step components receive values/handlers via props only.
 */
export const BusinessOnboard = () => {
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { saveSignupData } = useAuth();

  // Restore an in-progress snapshot once on mount (survives browser reloads).
  const persistedRef = React.useRef<Partial<IPersistedProgress> | null>(
    readPersistedProgress(),
  );
  const persisted = persistedRef.current;

  const [activeStep, setActiveStep] = React.useState(persisted?.activeStep ?? 0);
  const [showOTP, setShowOTP] = React.useState(persisted?.showOTP ?? false);
  const [loading, setLoading] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [resendTimer, setResendTimer] = React.useState(0);

  const [selectedCountryIso, setSelectedCountryIso] = React.useState(
    persisted?.selectedCountryIso ?? "IN",
  );
  const selectedCountryCode =
    COUNTRIES.find((c) => c.code === selectedCountryIso)?.dialCode || "+91";

  const [mobileExists, setMobileExists] = React.useState(false);
  const [mobileData, setMobileData] = React.useState<IMobileData>(
    persisted?.mobileData ?? {
      mobileNumber: "",
      otp: "",
      onboarding_uuid: "",
      token: "",
      loading: false,
    },
  );

  const [venues, setVenues] = React.useState<IVenue[]>(
    persisted?.venues && persisted.venues.length > 0
      ? persisted.venues
      : [createEmptyVenue()],
  );
  const [venueErrors, setVenueErrors] = React.useState<VenueErrors>({});

  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [mobileError, setMobileError] = React.useState<string | null>(null);
  const [otpError, setOtpError] = React.useState<string | null>(null);
  const [step1Submitted, setStep1Submitted] = React.useState(false);
  const [step3Submitted, setStep3Submitted] = React.useState(false);

  /* ---------------------------------------------------------------- */
  /* Venue validation (v2 requires name + both capacities)            */
  /* ---------------------------------------------------------------- */
  const validateVenues = (): boolean => {
    const newErrors: VenueErrors = {};
    let isValid = true;

    venues.forEach((venue, index) => {
      const errs: Partial<IVenue> = {};

      if (!venue.venue_name.trim()) {
        errs.venue_name = "Venue name is required.";
        isValid = false;
      }
      if (!venue.seating_capacity.trim()) {
        errs.seating_capacity = "Seating capacity is required.";
        isValid = false;
      } else if (Number(venue.seating_capacity) <= 0) {
        errs.seating_capacity = "Enter a valid seating capacity.";
        isValid = false;
      }
      if (!venue.floating_capacity.trim()) {
        errs.floating_capacity = "Floating capacity is required.";
        isValid = false;
      } else if (Number(venue.floating_capacity) <= 0) {
        errs.floating_capacity = "Enter a valid floating capacity.";
        isValid = false;
      }

      if (Object.keys(errs).length > 0) newErrors[index] = errs;
    });

    setVenueErrors(newErrors);
    return isValid;
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik<ISignupFormValues>({
    initialValues: persisted?.values ?? initialFormValues,
    validate: (values) => {
      const errs: any = {};

      // Step 1: Company Details
      if (activeStep === 1) {
        if (!values.company_outlet_name) {
          errs.company_outlet_name = "Company name is required.";
        }
        if (!values.company_outlet_address_line1) {
          errs.company_outlet_address_line1 = "Address line 1 is required.";
        }
        if (!values.company_outlet_address_city) {
          errs.company_outlet_address_city = "City is required.";
        }
        if (!values.company_outlet_address_state_or_province) {
          errs.company_outlet_address_state_or_province =
            "State is required.";
        }
        if (!values.company_outlet_address_country) {
          errs.company_outlet_address_country = "Country is required.";
        }
        if (!values.company_outlet_timezone) {
          errs.company_outlet_timezone = "Timezone is required.";
        }
      }

      // Step 3: User Profile
      if (activeStep === 3) {
        if (!values.first_name) {
          errs.first_name = "First name is required.";
        }
        if (!values.email) {
          errs.email = "Email is required.";
        } else if (
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(values.email)
        ) {
          errs.email = "Enter a valid email address.";
        }
        if (!values.user_password) {
          errs.user_password = "Password is required.";
        } else if (values.user_password.length < 6) {
          errs.user_password = "Password must be at least 6 characters.";
        }
        if (!values.confirm_password) {
          errs.confirm_password = "Confirm password is required.";
        } else if (values.user_password !== values.confirm_password) {
          errs.confirm_password = "Passwords do not match.";
        }
      }

      return errs;
    },
    onSubmit: async (values) => {
      setError(null);

      // Steps 0-2 advance via explicit handlers (onPrimary); the Formik submit
      // path only performs the final account creation on step 3.
      if (activeStep !== 3) return;

      try {
        setLoading(true);
        dispatch(saveLoaderProgress());

        const venuesPayload = venues.map((venue) => ({
          venue_name: venue.venue_name,
          venue_city: venue.venue_city,
          venue_state: venue.venue_state,
          venue_country: venue.venue_country,
          venue_pincode: venue.venue_pincode,
          venue_address: venue.venue_address,
          seating_capacity: Number(venue.seating_capacity),
          floating_capacity: Number(venue.floating_capacity),
        }));

        const logoPath = await uploadOnboardingFile({
          file: file,
          module_name: "COMPANY_OUTLET_LOGO",
          defaultReturn: values.logo || "",
          asPayload: { company_outlet_name: values.company_outlet_name },
          headers: { "onboarding-token": mobileData.token },
          onWarning: (message) => {
            dispatch(
              showMessage({
                type: "warning",
                message,
                displayAs: "snackbar",
              }),
            );
          },
        });

        const payload = {
          company_outlet_name: values.company_outlet_name,
          venues: venuesPayload,
          mobile: mobileData.mobileNumber,
          email: values.email,
          user_password: values.user_password,
          first_name: values.first_name,
          last_name: values.last_name || "",
          status: values.status,
          logo: logoPath,
          company_outlet_address_line1: values.company_outlet_address_line1,
          company_outlet_address_line2: values.company_outlet_address_line2,
          company_outlet_address_city: values.company_outlet_address_city,
          company_outlet_address_state_or_province:
            values.company_outlet_address_state_or_province,
          company_outlet_address_country: values.company_outlet_address_country,
          company_outlet_address_postal_code:
            values.company_outlet_address_postal_code,
          company_outlet_region: values.company_outlet_region,
          company_outlet_timezone: values.company_outlet_timezone,
        };

        const response = await baseAxios.post(`/user/signup-user`, payload, {
          headers: { "onboarding-token": mobileData.token },
        });

        const data = response.data.data.data;

        if (data) {
          // Onboarding succeeded — clear the persisted draft.
          try {
            sessionStorage.removeItem(ONBOARD_STORAGE_KEY);
          } catch {
            // ignore
          }
          saveSignupData(data, () => {
            navigate(REACT_APP_NAV_ROUTES.subscription.pricing);
          });
        }
      } catch (error: any) {
        setError(
          error.response?.data?.message || "An unexpected error occurred",
        );
      } finally {
        dispatch(saveLoaderCompleted());
        setLoading(false);
      }
    },
  });

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => {
    setError(null);
    if (activeStep === 0 && showOTP) {
      // Back out of OTP sub-state to the mobile entry.
      setShowOTP(false);
      setResendTimer(0);
      setMobileData((prev) => ({
        ...prev,
        mobileNumber: prev.mobileNumber.replace(selectedCountryCode, ""),
        otp: "",
      }));
      return;
    }
    if (activeStep === 0) {
      // On the mobile-entry step, back returns to the landing.
      navigate("/auth/signup");
      return;
    }
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  /* ---------------------------------------------------------------- */
  /* Mobile / OTP handlers                                            */
  /* ---------------------------------------------------------------- */
  const handleGenerateOTP = async () => {
    setMobileError(null);
    setOtpError(null);
    setMobileExists(false);
    const fullMobileNumber = selectedCountryCode + mobileData.mobileNumber;

    if (!mobileData.mobileNumber) {
      setMobileError("Mobile number is required.");
      return;
    } else if (!/^[0-9]+$/.test(mobileData.mobileNumber)) {
      setMobileError("Mobile number must contain only digits.");
      return;
    } else if (mobileData.mobileNumber.length !== 10) {
      setMobileError("Mobile number must be exactly 10 digits.");
      return;
    }

    try {
      setError(null);
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/generate-otp-mobile`,
        { mobile: fullMobileNumber },
      );
      const data = response.data.data;

      if (
        data?.user_exists ||
        data?.exists ||
        response.data?.user_exists ||
        response.data?.exists
      ) {
        setMobileExists(true);
        setShowOTP(false);
        setMobileData((prev) => ({
          ...prev,
          loading: false,
          otp: "",
          onboarding_uuid: "",
          token: "",
        }));
        setError("Mobile number already exists. Please try a different number.");
        return;
      }

      setMobileData((prev) => ({
        ...prev,
        onboarding_uuid: data.onboarding_uuid,
        loading: false,
        mobileNumber: fullMobileNumber,
      }));
      setShowOTP(true);
      setResendTimer(60);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.data?.message ||
        "";
      const errorString = errorMessage.toLowerCase();

      if (
        error.response?.status === 401 ||
        errorString.includes("already exists") ||
        errorString.includes("number already registered") ||
        errorString.includes("reset the password") ||
        errorString.includes("you can login") ||
        errorString.includes("either you reset") ||
        errorString.includes("login it") ||
        error.response?.status === 409 ||
        (error.response?.status === 400 && errorString.includes("exists"))
      ) {
        setMobileExists(true);
        setShowOTP(false);
        setMobileData((prev) => ({
          ...prev,
          otp: "",
          onboarding_uuid: "",
          token: "",
        }));
        if (
          errorMessage &&
          (errorString.includes("reset") || errorString.includes("login"))
        ) {
          setError(errorMessage);
        } else {
          setError(
            "Mobile number already exists. Please try a different number.",
          );
        }
      } else {
        setError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.data?.message ||
          "An unexpected error occurred",
        );
      }
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleResendOTP = async () => {
    setOtpError(null);
    setError(null);
    setSuccessMessage(null);
    try {
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/generate-otp-mobile`,
        { mobile: mobileData.mobileNumber },
      );
      const data = response.data.data;
      setMobileData((prev) => ({
        ...prev,
        onboarding_uuid: data.onboarding_uuid,
        loading: false,
        otp: "",
      }));
      setResendTimer(60);
      setSuccessMessage("OTP resent successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || "An unexpected error occurred");
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleVerifyOTP = async () => {
    setOtpError(null);
    if (!mobileData.otp) {
      setOtpError("OTP is required.");
      return;
    } else if (!/^[0-9]{4,6}$/.test(mobileData.otp)) {
      setOtpError("Enter a valid OTP.");
      return;
    }
    try {
      setError(null);
      setMobileData((prev) => ({ ...prev, loading: true }));
      const response = await baseAxios.post(
        `/authentication/validate-otp-get-token-mobile`,
        {
          mobile: mobileData.mobileNumber,
          otp: mobileData.otp,
          onboarding_uuid: mobileData.onboarding_uuid,
        },
      );
      const data = response.data.data;
      setMobileData((prev) => ({
        ...prev,
        token: data.onboarding_token,
        loading: false,
      }));
      // Auto-fill the company country from the mobile step's country if unset.
      if (!values.company_outlet_address_country) {
        const mobileCountry = COUNTRIES.find(
          (c) => c.code === selectedCountryIso,
        );
        if (mobileCountry) {
          setValues((prev) => ({
            ...prev,
            company_outlet_address_country: mobileCountry.name,
          }));
        }
      }
      setShowOTP(true);
      handleNext();
    } catch (error: any) {
      setError(error.response?.data?.message || "An unexpected error occurred");
    } finally {
      setMobileData((prev) => ({ ...prev, loading: false }));
    }
  };

  /* ---------------------------------------------------------------- */
  /* Venue + address handlers                                         */
  /* ---------------------------------------------------------------- */
  const updateVenue = (index: number, field: keyof IVenue, value: string) => {
    setVenues((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (venueErrors[index]?.[field]) {
      setVenueErrors((prev) => {
        const next = { ...prev };
        if (next[index]) {
          const row = { ...next[index] };
          delete row[field];
          if (Object.keys(row).length === 0) {
            delete next[index];
          } else {
            next[index] = row;
          }
        }
        return next;
      });
    }
  };

  const addVenue = () => {
    setVenues((prev) => [...prev, createEmptyVenue()]);
  };

  const removeVenue = (index: number) => {
    setVenues((prev) => {
      if (prev.length === 1) return prev; // keep at least one venue
      return prev.filter((_, i) => i !== index);
    });
    // Drop errors for the removed row and reindex the rest.
    setVenueErrors((prev) => {
      const next: VenueErrors = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < index) next[i] = prev[i];
        else if (i > index) next[i - 1] = prev[i];
      });
      return next;
    });
  };

  const handleCompanyAddressChange = (data: ILocationResponsePayload) => {
    setValues((prev) => ({
      ...prev,
      company_outlet_name: data.place || prev.company_outlet_name || "",
      company_outlet_address_line1: data.address_line1 || "",
      company_outlet_address_line2: data.address_line2 || "",
      company_outlet_address_city: data.city || "",
      company_outlet_address_state_or_province: data.state || "",
      company_outlet_address_country: data.country || "",
      company_outlet_address_postal_code: data.postal_code || "",
      company_outlet_region: data.region || "",
    }));
  };

  const handleStep1Submit = async () => {
    setStep1Submitted(true);
    // Validate the step-1 required fields directly against current values
    // (avoids relying on Formik's activeStep-gated validate closure).
    const hasErrors =
      !values.company_outlet_name?.trim() ||
      !values.company_outlet_address_line1?.trim() ||
      !values.company_outlet_address_city?.trim() ||
      !values.company_outlet_address_state_or_province?.trim() ||
      !values.company_outlet_address_country?.trim() ||
      !values.company_outlet_timezone?.trim();

    if (!hasErrors) {
      setError(null);
      handleNext();
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  const handleStep3Submit = async () => {
    setStep3Submitted(true);
    // Validate step-3 required fields directly against current values.
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const hasErrors =
      !values.first_name?.trim() ||
      !values.email?.trim() ||
      !emailRegex.test(values.email) ||
      !values.user_password ||
      values.user_password.length < 6 ||
      !values.confirm_password ||
      values.user_password !== values.confirm_password;

    if (!hasErrors) {
      setError(null);
      handleSubmit();
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  const handleLogin = () =>
    navigate("/auth/login", { state: { mobile: mobileData.mobileNumber } });
  const handleRecoverPassword = () =>
    navigate("/recover-password", {
      state: { mobile: mobileData.mobileNumber },
    });

  /* ---------------------------------------------------------------- */
  /* Effects                                                          */
  /* ---------------------------------------------------------------- */
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Persist in-progress onboarding to sessionStorage so a browser reload
  // restores where the user left off (cleared on successful signup).
  React.useEffect(() => {
    try {
      const snapshot: IPersistedProgress = {
        activeStep,
        showOTP,
        selectedCountryIso,
        mobileData,
        values,
        venues,
      };
      sessionStorage.setItem(ONBOARD_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // ignore storage failures (private mode / quota)
    }
  }, [activeStep, showOTP, selectedCountryIso, mobileData, values, venues]);

  // Warn on browser refresh / tab close while onboarding is in progress, so
  // entered data isn't lost. The browser shows its own generic confirmation.
  React.useEffect(() => {
    const hasProgress =
      activeStep > 0 ||
      showOTP ||
      !!mobileData.mobileNumber ||
      !!values.company_outlet_name ||
      !!values.email ||
      !!values.first_name ||
      venues.some(
        (v) =>
          v.venue_name || v.seating_capacity || v.floating_capacity,
      );

    if (!hasProgress) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for the confirmation prompt in Chrome/Firefox.
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [
    activeStep,
    showOTP,
    mobileData.mobileNumber,
    values.company_outlet_name,
    values.email,
    values.first_name,
    venues,
  ]);

  React.useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      const decoded = decodeUserToken(token);
      setMobileData((prev) => ({
        ...prev,
        token,
        mobileNumber: decoded.mobile || "",
        onboarding_uuid: decoded.onboarding_uuid || "",
      }));
      setActiveStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ---------------------------------------------------------------- */
  /* Derived header + footer                                          */
  /* ---------------------------------------------------------------- */
  const isOtpSubState = activeStep === 0 && showOTP;
  const stepMeta = businessOnboardSteps[activeStep];
  const header = isOtpSubState
    ? {
      eyebrow: mobileOtpMeta.eyebrow,
      title: mobileOtpMeta.title,
      subtitle: buildOtpSubtitle(mobileData.mobileNumber),
    }
    : {
      eyebrow: stepMeta.eyebrow,
      title: stepMeta.title,
      subtitle: stepMeta.subtitle,
    };

  // Progress fills proportionally per step; step 1 already shows ~1/4,
  // the OTP sub-state nudges it halfway between steps 1 and 2.
  const progress =
    (activeStep + 1 + (isOtpSubState ? 0.5 : 0)) / (TOTAL_STEPS + 1);

  const primaryLabel = isOtpSubState
    ? primaryCtaLabels.otp
    : activeStep === 0
      ? primaryCtaLabels.mobile
      : activeStep === 1
        ? primaryCtaLabels.company
        : activeStep === 2
          ? primaryCtaLabels.venue
          : primaryCtaLabels.profile;

  // All steps run explicit handlers (validate-then-advance) so required
  // fields are enforced before moving on.
  const primaryType: "button" | "submit" = "button";

  const handleStep2Submit = () => {
    if (!validateVenues()) {
      setError("Please fill in all required venue fields correctly.");
      return;
    }
    setError(null);
    handleNext();
  };

  const onPrimary = () => {
    if (activeStep === 0) {
      isOtpSubState ? handleVerifyOTP() : handleGenerateOTP();
    } else if (activeStep === 1) {
      handleStep1Submit();
    } else if (activeStep === 2) {
      handleStep2Submit();
    } else if (activeStep === 3) {
      handleStep3Submit();
    }
  };

  const showBack = true;
  // On the mobile-entry step with an "exists" branch, hide the primary CTA
  // (Login / Recover buttons render inside the step instead).
  const hidePrimaryForExists = activeStep === 0 && !showOTP && mobileExists;

  const renderStep = () => {
    if (activeStep === 0) {
      return showOTP ? (
        <OtpStep
          otp={mobileData.otp}
          otpError={otpError}
          resendTimer={resendTimer}
          loading={mobileData.loading}
          onOtpChange={(value) =>
            setMobileData((prev) => ({ ...prev, otp: value.slice(0, 6) }))
          }
          onResend={handleResendOTP}
        />
      ) : (
        <MobileStep
          mobileNumber={mobileData.mobileNumber}
          selectedCountryIso={selectedCountryIso}
          selectedCountryCode={selectedCountryCode}
          mobileError={mobileError}
          mobileExists={mobileExists}
          onCountryChange={(country: ICountry) =>
            setSelectedCountryIso(country.code)
          }
          onMobileChange={(digits) => {
            setMobileData((prev) => ({ ...prev, mobileNumber: digits }));
            if (mobileExists) {
              setMobileExists(false);
              setError(null);
            }
          }}
          onLogin={handleLogin}
          onRecoverPassword={handleRecoverPassword}
          onBack={handleBack}
        />
      );
    }
    if (activeStep === 1) {
      return (
        <CompanyStep
          values={values}
          errors={errors}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          onLocationChange={handleCompanyAddressChange}
          submitAttempted={step1Submitted}
        />
      );
    }
    if (activeStep === 2) {
      return (
        <VenueStep
          venues={venues}
          venueErrors={venueErrors}
          onChange={updateVenue}
          onAdd={addVenue}
          onRemove={removeVenue}
        />
      );
    }
    return (
      <ProfileStep
        values={values}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword((s) => !s)}
        submitAttempted={step3Submitted}
      />
    );
  };

  return (
    <OnboardLayout progress={progress} >
      <OnboardStepper activeStep={activeStep} labels={businessOnboardStepLabels} />

      <form onSubmit={handleSubmit} autoComplete="off">
        <div style={{ marginTop: 24 }}>
          <StepHeader
            eyebrow={header.eyebrow}
            title={header.title}
            subtitle={header.subtitle}
          />

          {error && (
            <CustomAlert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
              {error}
            </CustomAlert>
          )}
          {successMessage && (
            <CustomAlert severity="success" sx={{ mb: 2, borderRadius: "10px" }}>
              {successMessage}
            </CustomAlert>
          )}

          {renderStep()}

          {!hidePrimaryForExists && (
            <StepFooter
              primaryLabel={primaryLabel}
              primaryType={primaryType}
              onPrimary={onPrimary}
              onBack={handleBack}
              showBack={showBack}
              loading={loading || mobileData.loading}
            />
          )}
        </div>
      </form>
    </OnboardLayout>
  );
};

export default BusinessOnboard;
